import { Box, Stack } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { PageState } from '../../shared/data/PageState';
import { DetailHeader } from '../../shared/detail/DetailHeader';
import { MetadataList } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { BeforeAfterPanel, SafeEntriesPanel } from './BeforeAfterPanel';
import { toAuditPageError } from './AuditLogPage';
import { auditRepositoryToken } from './repository';

export function AuditDetailPage({ dataSource }: { readonly dataSource: DataSource }) {
  const { auditId = '' } = useParams();
  const repository = useRepository(auditRepositoryToken);
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refresh, setRefresh] = useState(0);
  const resource = useResource(
    (signal) => repository.getAuditLog(auditId, signal),
    [repository, auditId, refresh],
    () => false,
  );
  return (
    <Box sx={{ minWidth: 0 }}>
      <PageState
        error={toAuditPageError(resource.error, t('audit.errors.loadDetail'))}
        onRetry={() => setRefresh((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={2}>
            <DetailHeader
              backTo={paths.audit}
              dataSource={dataSource}
              subtitle={`${resource.data.action} · ${resource.data.entityType}`}
              title={resource.data.id}
            />
            <MetadataList
              items={[
                {
                  label: t('audit.fields.occurredAt'),
                  value: `${formatTimestamp(resource.data.occurredAt, 'Asia/Bangkok', language).display} (${formatTimestamp(resource.data.occurredAt, 'Asia/Bangkok', language).originalOffset})`,
                },
                { label: t('audit.fields.action'), value: resource.data.action },
                { label: t('audit.fields.entityType'), value: resource.data.entityType },
                {
                  label: t('audit.fields.entityId'),
                  value: resource.data.entityId ?? t('common.notAvailable'),
                },
                {
                  label: t('audit.fields.reason'),
                  value: resource.data.reason ?? t('common.notAvailable'),
                },
                {
                  label: t('audit.fields.principalActorId'),
                  value: resource.data.principalActorId,
                },
                {
                  label: t('audit.fields.principalRole'),
                  value: resource.data.principalRole ?? t('common.notAvailable'),
                },
                { label: t('audit.fields.serviceActorId'), value: resource.data.serviceActorId },
                {
                  label: t('audit.fields.partnerId'),
                  value: resource.data.partnerId ?? t('common.notAvailable'),
                },
                {
                  label: t('audit.fields.agentId'),
                  value: resource.data.agentId ?? t('common.notAvailable'),
                },
                {
                  label: t('audit.fields.correlationId'),
                  value: resource.data.correlationId ?? t('common.notAvailable'),
                },
                { label: t('audit.fields.requestId'), value: resource.data.publicRequestId },
                {
                  label: t('audit.fields.sourceIp'),
                  value: resource.data.sourceIp ?? t('common.notAvailable'),
                },
              ]}
            />
            <BeforeAfterPanel
              after={resource.data.after}
              before={resource.data.before}
              title={t('audit.sections.beforeAfter')}
            />
            <SafeEntriesPanel
              record={resource.data.metadata}
              title={t('audit.sections.metadata')}
            />
          </Stack>
        )}
      </PageState>
    </Box>
  );
}
